export const S3_BUCKET = 'migration-sourcedatabases';
export const S3_REGION = 'eu-west-1';
/**
 * Pre-approved destination buckets for imports. This is the security boundary:
 * an admin may only switch the active destination to one of these, and the CDK
 * IAM grants are scoped to exactly this set (see infra/lib/compute.nested-stack.ts).
 * Add a new bucket here AND redeploy the infra stack before it can be selected.
 */
export const ALLOWED_DESTINATION_BUCKETS = [
    { bucket: S3_BUCKET, region: S3_REGION },
    // Add additional pre-approved buckets here, e.g.
    // { bucket: 'atlas-import-data-us', region: 'us-east-1' },
];
/**
 * Client data export buckets. Exported CSVs + copied attachments land in
 * EXPORT_BUCKET (served by the shared SFTP server); the agency's existing file
 * attachments are copied server-side out of EXPORT_ATTACHMENTS_BUCKET, which
 * is Atlas's production uploads bucket and is only ever read. The CDK grants
 * are built from these (see infra/lib/compute.nested-stack.ts).
 */
export const EXPORT_BUCKET = 'export-data-atlas';
export const EXPORT_ATTACHMENTS_BUCKET = 'prod-atlas-uploads-465358817960';
/** The default destination used when no override has been saved. */
export const DEFAULT_DESTINATION = {
    bucket: S3_BUCKET,
    region: S3_REGION,
};
/** Returns true when the given bucket name is in the pre-approved allow-list. */
export function isAllowedDestinationBucket(bucket) {
    return ALLOWED_DESTINATION_BUCKETS.some((d) => d.bucket === bucket);
}
/** Looks up the allow-list entry (with its region) for a bucket name, if any. */
export function findAllowedDestination(bucket) {
    return ALLOWED_DESTINATION_BUCKETS.find((d) => d.bucket === bucket);
}
/**
 * Source systems an import can come from. Each has its own mounting/parsing
 * logic downstream, so the engineer must pick the correct one per import.
 * Values match the source folder slugs in S3.
 */
export const SOURCE_SYSTEMS = [
    'adapt_rec',
    'bullhorn',
    'ezekia',
    'filefinder',
    'firefish',
    'futurelink',
    'gem',
    'invenias',
    'itris',
    'jobadder',
    'job_science',
    'loxo',
    'manatal',
    'microdec',
    'pc_recruiter',
    'recruit_crm',
    'recruitee_crm',
    'recruiterflow',
    'salesforce',
    'seven20',
    'shadow_one',
    'targetrecruit',
    'thrive',
    'top_echelon',
    'tracker',
    'vincere',
    'voyager_infinity',
];
/** Display labels for the source systems (falls back to the slug). */
export const SOURCE_SYSTEM_LABELS = {
    adapt_rec: 'Adapt Recruitment',
    bullhorn: 'Bullhorn',
    ezekia: 'Ezekia',
    filefinder: 'FileFinder',
    firefish: 'Firefish',
    futurelink: 'Futurelink Global',
    gem: 'Gem',
    invenias: 'Invenias',
    itris: 'Itris',
    jobadder: 'JobAdder',
    job_science: 'JobScience',
    loxo: 'Loxo',
    manatal: 'Manatal',
    microdec: 'Microdec Profile',
    pc_recruiter: 'PC Recruiter',
    recruit_crm: 'Recruit CRM',
    recruitee_crm: 'Recruitee CRM',
    recruiterflow: 'Recruiterflow',
    salesforce: 'Salesforce',
    seven20: 'Seven20',
    shadow_one: 'Shadow One',
    targetrecruit: 'TargetRecruit',
    thrive: 'Thrive',
    top_echelon: 'Top Echelon',
    tracker: 'Tracker',
    vincere: 'Vincere',
    voyager_infinity: 'Voyager Infinity',
};
/**
 * Source systems that deliver their export as a single downloadable URL (a
 * presigned link to a .zip) rather than over FTP/SFTP. These jobs skip the FTP
 * fields entirely — the worker fetches the URL and streams the archive into S3,
 * then the existing per-source mount method takes over (e.g. Loxo JSONL).
 */
export const URL_INTAKE_SOURCES = ['loxo'];
/** True when a source system is ingested from a download URL rather than FTP. */
export function isUrlIntakeSource(sourceSystem) {
    return URL_INTAKE_SOURCES.includes(sourceSystem);
}
export const EC2_BASE_PATH = 'C:\\migration-data';
/** Lowercase a value and strip anything unsafe for an S3 key segment. */
function slugifySegment(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_') // spaces → underscore
        .replace(/[^a-z0-9_-]/g, '') // drop anything else unsafe
        .replace(/_+/g, '_') // collapse repeated underscores
        .replace(/^[_-]+|[_-]+$/g, '');
}
/**
 * Build the S3 prefix for a job. Files land under `<crm>/<client>/files`, where
 * <crm> is the source system (CRM) the data comes from and <client> is the
 * client name — both lowercased with spaces replaced by underscores and any
 * other unsafe characters removed.
 */
export function buildS3Prefix(clientName, sourceSystem) {
    const crm = slugifySegment(sourceSystem);
    const slug = slugifySegment(clientName);
    return `${crm}/${slug}/files`;
}
/** Build the EC2 incoming folder path for a Bullhorn job. */
export function buildEc2IncomingPath(jobId) {
    return `${EC2_BASE_PATH}\\${jobId}\\incoming`;
}
/** Build the EC2 decrypted folder path for a Bullhorn job. */
export function buildEc2DecryptedPath(jobId) {
    return `${EC2_BASE_PATH}\\${jobId}\\decrypted`;
}
//# sourceMappingURL=constants.js.map